import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';

import { RankRewardTitle } from 'components/leaderboard/CumulativeRankRewards/RankRewardTitle';
import { Ranking } from 'components/leaderboard/RankRewards/Ranking';
import {
  RewardRange,
  RewardRangeVariant,
} from 'components/leaderboard/RewardRange';
import { CompactSo5Lineup } from 'components/lineup/CompactSo5Lineup';

import type { RankingFullLeaderboardRow_so5Ranking } from './__generated__/index.graphql';

type Props = {
  so5Ranking: RankingFullLeaderboardRow_so5Ranking;
  isMyRanking: boolean;
  displayLineup?: boolean;
  scoreDiff: number;
  onClick?: () => void;
};

export const RankingFullLeaderboardRow = ({
  so5Ranking,
  isMyRanking,
  displayLineup,
  scoreDiff,
  onClick,
}: Props) => {
  const { ranking, eligibleOrSo5Rewards } = so5Ranking;

  const rewardConfigs = eligibleOrSo5Rewards.flatMap(so5RewardOrConfig =>
    isType(so5RewardOrConfig, 'So5RewardConfig')
      ? so5RewardOrConfig.rewardConfigs
      : so5RewardOrConfig.rewards.map(reward => reward.config)
  );

  return (
    <Vertical
      gap={2}
      className={classNames(`ranking-${ranking}`, { isMyRanking })}
    >
      <RewardRange
        variant={RewardRangeVariant.BLOCK}
        title={
          <RankRewardTitle
            startRank={ranking}
            endRank={ranking}
            startPct={null}
            endPct={null}
            scoreDistanceFromMyRanking={scoreDiff}
          />
        }
        rewards={rewardConfigs.map(rewardConfig => (
          <AnyRewardConfig
            key={rewardConfig.id}
            anyRewardConfig={rewardConfig}
            variant={Variant.CHIP}
          />
        ))}
      />
      {!!displayLineup && (
        <Ranking
          so5Ranking={so5Ranking}
          isMyRanking={isMyRanking}
          onClick={onClick}
          displayRank={false}
        >
          <CompactSo5Lineup so5Lineup={so5Ranking.so5Lineup} />
        </Ranking>
      )}
    </Vertical>
  );
};

RankingFullLeaderboardRow.fragments = {
  so5Ranking: gql`
    fragment RankingFullLeaderboardRow_so5Ranking on So5Ranking {
      id
      ranking
      eligibleDivisionChange
      score
      eligibleOrSo5Rewards {
        ... on So5RewardConfig {
          id
          rewardConfigs {
            id
            ...AnyRewardConfig_anyRewardConfigInterface
          }
        }
        ... on So5Reward {
          slug
          rewards {
            id
            config {
              id
              ...AnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
      }
      so5Lineup {
        id
        ...CompactSo5Lineup_so5Lineup
      }
      ...RankRewardsRanking_so5Ranking
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${CompactSo5Lineup.fragments.so5Lineup}
    ${Ranking.fragments.so5Ranking}
  ` as TypedDocumentNode<RankingFullLeaderboardRow_so5Ranking>,
};
